import React, { useState, useEffect, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import workSpaceCss from "../../css/workSpaceOverView.module.css";
import FSSideMenu from "../../components/standalonefs/FSSideMenu";
import FSTopBar from "../../components/standalonefs/FSTopBar";

import swal from "sweetalert";
import {
  deletePopulateFSApiHandler,
  getPopulateFSDetailsApiHandler,
  updatePopulateFSApiHandler,
} from "../../../WorkSpace/Components/ApiServices/apiHandler";
import { motion } from "framer-motion";
import commoncss from "../../css/common.module.css";
import bookclosurecss from "../../css/bookclosure.module.css";
import QuillReactEditor from "../../components/QuillReactEditor";
import FSTable from "./FSTable";
import ViewRPTTransactions from "./ViewRPTTransactions";


const StandaloneFinancialStatement = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [activeCat, setActiveCat] = useState("BS");
  const [isNotes, setIsNotes] = useState(false);
  const [loadData, setLoadData] = useState([]);
  const [notesId, setNotesId] = useState(null);
  const [redirectNote, setRedirectNote] = useState(null);
  const [subMenuId, setSubMenuId] = useState(null);
  const [subgroupId, setSubGroupId] = useState(null);
  const [columns, setColumns] = useState([]);
  const [subMenu, setSubMenu] = useState(null);
  const [rows, setRows] = useState([]);
  const [notesData, setNotesData] = useState(null);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [fsNotesResponseData, setFsNotesResponseData] = useState(null);

  const project_id = localStorage.getItem("project_id");

  // ── Sync rows from the correct source depending on category ───────────────
  useEffect(() => {
    if (activeCat === "BS" || activeCat === "PL") {
      const result = transformData(loadData ?? []);
      setRows(result ?? []);
    } else if (["general notes", "BS notes", "PL notes", "disclosure"].includes(activeCat)) {
      setRows(fsNotesResponseData ?? []);
    } else {
      setRows(loadData ?? []);
    }
  }, [loadData, fsNotesResponseData, activeCat]);

  // ── Set columns / isNotes / subMenu based on activeCat and rows ───────────
  useEffect(() => {
    setColumns([]);

    // BS / PL
    if (activeCat === "BS" || activeCat === "PL") {
      setIsNotes(false);
      setSubMenu(null);
      setColumns([
        {
          label: "Particular",
          field: "particular",
          align: "left",
          editable: false,
          width: "40%",
        },
        {
          label: "Notes",
          field: "notes",
          align: "center",
          editable: false,
          type: "redirect",
          width: "15%",
        },
        {
          label: "Current Year",
          field: "currentYear",
          align: "right",
          editable: false,
          type: "number",
          width: 200,
          minWidth: 200,
          maxWidth: 200,
        },
        {
          label: "Previous Year",
          field: "previousYear",
          align: "right",
          editable: false,
          type: "number",
          width: 200,
          minWidth: 200,
          maxWidth: 200,
        },
      ]);
      return;
    }

    // SOCIE / Cashflow
    if (["socie", "cashflow"].includes(activeCat)) {
      setIsNotes(false);
      setSubMenu(null);

      if (rows.length > 0) {
        const catRows = Object.keys(rows[0] || {});

        const tableCols = catRows
          .filter(
            (item) =>
              item !== "_id" && item !== "lineType" && item !== "references"
          )
          .map((item) => {
            let alignValue = "left";

            if (
              activeCat === "cashflow" &&
              (item === "currentYear" || item === "previousYear")
            ) {
              alignValue = "right";
            }

            if (activeCat === "socie" && item !== "particular") {
              alignValue = "right";
            }

            return {
              label: item,
              field: item,
              align: alignValue,
              editable: true,
              type: "text",
              width:
                item === "currentYear" || item === "previousYear"
                  ? "200px"
                  : item === "particular"
                  ? "300px"
                  : "",
            };
          });

        setColumns(tableCols);
      }
      return;
    }

    // Notes categories
    if (
      ["general notes", "BS notes", "PL notes", "disclosure", "checklist"].includes(activeCat)
    ) {
      setIsNotes(true);
      const submenuArray = rows.map((item) => ({
        label: item.particular,
        value: item._id,
        subGroup: item.subgroup,
      }));

      setSubMenu({
        category: activeCat,
        submenu: submenuArray,
      });
      setColumns([]);
      return;
    }

    setIsNotes(false);
    setSubMenu(null);
    setColumns([]);
  }, [activeCat, rows]);

  // ── Auto-select first submenu item ────────────────────────────────────────
  useEffect(() => {
    if (!isNotes) return;
    if (!subMenu?.submenu?.length) return;
    if (subMenuId) return;

    const first = subMenu.submenu[0];
    setSubMenuId(first.value);
  }, [isNotes, activeCat, subMenu, subMenuId]);

  const handleRowsChange = (updatedRows) => {};

  const menuItems = [
    { label: "Balance Sheet", value: "BS" },
    { label: "P&L Statement", value: "PL" },
    { label: "General Notes", value: "general notes" },
    { label: "CashFlow", value: "cashflow" },
    { label: "SOCIE", value: "socie" },
    { label: "Balance Sheet Notes", value: "BS notes" },
    { label: "P&L Statement Notes", value: "PL notes" },
    { label: "Disclosures", value: "disclosure" },
    { label: "Disclosures Checklist", value: "checklist" },
  ];

  const getPopulateFS = (cat) => {
    const getParams = {
      category: cat,
      projectID: project_id,
    };
    getPopulateFSDetailsApiHandler(getParams)
      .then((response) => {
        if (response?.data?.success === true) {
          const catData = response?.data?.data;
          setLoadData(catData);
        }
      })
      .catch((error) => {
        setLoadData([]);
      });
  };

  // ── Fetch data whenever activeCat changes ─────────────────────────────────
  useEffect(() => {
    setColumns([]);
    setSubMenu(null);
    setIsNotes(false);
    setNotesData(null);
    setSubGroupId(null);
    setSubMenuId(null);
    getPopulateFS(activeCat);

    if (["BS notes", "PL notes", "general notes", "disclosure"].includes(activeCat)) {
      getPopulateFSNotes(activeCat);
    }
  }, [activeCat]);

  const debounceTimer = useRef(null);

  const handleTableEdit = ({ _id, key, value, type = "table", tableId }) => {
    const params = {
      category: activeCat,
      projectID: project_id,
    };

    if (isNotes && type !== "text") {
      params["groupID"] = subMenuId;
      if (activeCat === "checklist") {
        params["subgroupID"] = _id;
      } else {
        params["subgroupID"] = tableId;
        params["rowID"] = _id;
      }
    } else {
      if (
        activeCat === "BS" ||
        activeCat === "PL" ||
        activeCat === "socie" ||
        activeCat === "cashflow"
      ) {
        params["groupID"] = _id;
      }
    }

    const payload = {};

    if (isNotes && type === "text") {
      params["groupID"] = subMenuId;
      params["subgroupID"] = _id;
      payload["textRaw"] = key;
      payload["textPlain"] = value;
    } else {
      payload[key] = value;
    }

    updatePopulateFSApiHandler(params, payload)
      .then((response) => {
        if (response?.data?.success === true) {
          if (isNotes) {
            if (type === "text") {
              // ── Update fsNotesResponseData (source of truth for notes) ────
              setFsNotesResponseData((prev) =>
                (prev || []).map((group) => {
                  if (group._id !== subMenuId) return group;
                  return {
                    ...group,
                    subgroup: (group.subgroup || group.subGroup || []).map(
                      (sg) => {
                        if (sg._id !== _id) return sg;
                        return {
                          ...sg,
                          textRaw: payload.textRaw,
                          textPlain: payload.textPlain,
                        };
                      }
                    ),
                  };
                })
              );
            } else {
              // ── Update fsNotesResponseData (source of truth for notes) ────
              setFsNotesResponseData((prev) =>
                (prev || []).map((group) => {
                  if (group._id !== subMenuId) return group;
                  return {
                    ...group,
                    subgroup: (group.subgroup || group.subGroup || []).map(
                      (sg) => {
                        if (sg._id !== subgroupId) return sg;
                        return {
                          ...sg,
                          table: (sg.table || []).map((row) =>
                            row._id === _id ? { ...row, [key]: value } : row
                          ),
                        };
                      }
                    ),
                  };
                })
              );
            }
          } else {
            // Non-notes tables (BS, PL, socie, cashflow)
            setRows((prev) =>
              prev.map((row) =>
                row._id === _id ? { ...row, [key]: value } : row
              )
            );
          }
        } else {
          swal(
            "Error",
            response?.data?.error || "Unable to update the data",
            "error"
          );
        }
      })
      .catch((error) => {
        swal(
          "Error",
          error?.response?.data?.error || "Unable to save the data",
          "error"
        );
      });
  };

  const handleTableEditDebounced = (args) => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      handleTableEdit(args);
    }, 2000);
  };

  useEffect(() => {
    return () => clearTimeout(debounceTimer.current);
  }, []);

 const ShowDetails = () => {

  const formatIndianNumber = (val) => {
    if (val == null) return "";
    const num = Number(val);
    if (isNaN(num)) return val;

    if (num < 0) {
      return "(" + Math.abs(num).toLocaleString("en-IN") + ")";
    }
    return num.toLocaleString("en-IN");
  };

  const formattedRows =
    activeCat === "socie"
      ? rows.map((row) => {
          const newRow = { ...row };

          Object.keys(newRow).forEach((key) => {
            if (
              key !== "_id" &&
              key !== "lineType" &&
              key !== "references" &&
              newRow[key] !== null &&
              newRow[key] !== undefined &&
              !isNaN(Number(newRow[key]))   
            ) {
              newRow[key] = formatIndianNumber(newRow[key]);
            }
          });

          return newRow;
        })
      : rows;

  return (
    <FSTable
      columns={columns}
      rows={formattedRows}  
      onRowsChange={handleRowsChange}
      onCellChangeImmediate={handleTableEditDebounced}
      redirectTo={redirectTo}
    />
  );
};

  const ShowChecklist = ({ subMenuId }) => {
    if (!subMenu) return null;

    const selNote = subMenu.submenu.find((item) => item.value === subMenuId);
    const rows = selNote?.subGroup || [];

    const checklistColumns = [
      {
        label: "Paragraph reference",
        field: "reference",
        align: "left",
        editable: false,
        type: "popup",
      },
      {
        label: "Disclosure",
        field: "notes",
        align: "left",
        maxWidth: 200,
        editable: false,
        type: "text",
      },
      {
        label: "Yes",
        field: "value",
        optionValue: "Y",
        align: "center",
        editable: true,
        type: "checkbox",
      },
      {
        label: "No",
        field: "value",
        optionValue: "N",
        align: "center",
        editable: true,
        type: "checkbox",
      },
      {
        label: "NA",
        field: "value",
        optionValue: "NA",
        align: "center",
        editable: true,
        type: "checkbox",
      },
      {
        label: "Remarks",
        field: "remark",
        align: "left",
        editable: true,
        type: "text",
        placeholder: "Enter remarks here...",
      },
    ];

    return (
      <>
        <FSTable
          columns={checklistColumns}
          rows={rows}
          onRowsChange={handleRowsChange}
          onCellChangeImmediate={handleTableEditDebounced}
          redirectTo={redirectTo}
        />
      </>
    );
  };

  const getPopulateFSNotes = async (cat) => {
    try {
      const getParams = {
        category: cat,
        projectID: project_id,
      };

      const response = await getPopulateFSDetailsApiHandler(getParams);

      if (response?.data?.success === true) {
        setFsNotesResponseData(response.data.data);
        return response.data.data;
      }

      return [];
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const redirectTo = async (arg) => {
    let subNotesCat = null;

    if (activeCat === "BS") {
      setActiveCat("BS notes");
      subNotesCat = "BS notes";
    }
    if (activeCat === "PL") {
      setActiveCat("PL notes");
      subNotesCat = "PL notes";
    }

    if (subNotesCat) {
      const data = await getPopulateFSNotes(subNotesCat);
      const resultId = data.find((item) => item.notes === arg)?._id;
      setRedirectNote(resultId || null);
    }
  };

  // ── FIX: depend on fsNotesResponseData + subMenuId, NOT on rows ───────────
  // Previously `rows` was in the dep array which caused this to re-run
  // whenever rows changed (often), and at that moment fsNotesResponseData
  // could still be null → selNote = undefined → notesData wiped incorrectly.
  useEffect(() => {
    console.log("=== subMenuId ===", subMenuId);
    console.log("=== fsNotesResponseData ===", fsNotesResponseData);
    console.log("=== isNotes ===", isNotes);
    console.log("=== activeCat ===", activeCat);

    if (!isNotes || activeCat === "checklist" || !subMenuId) {
      setNotesData(null);
      return;
    }
    const source = fsNotesResponseData ?? [];
    const selNote = source.find((item) => item._id === subMenuId) || null;
    
    console.log("=== selNote found ===", selNote);
    setNotesData(selNote);
  }, [isNotes, activeCat, subMenuId, fsNotesResponseData]);
  // ─────────────────────────────────────────────────────────────────────────

  // ── Auto-select first table subgroup within a note ────────────────────────
  useEffect(() => {
    if (!isNotes || activeCat === "checklist" || !notesData) return;

    const subGroups = notesData.subgroup || notesData.subGroup || [];
    const firstTableSg = subGroups.find(
      (sg) => (sg?.dataType ?? sg?.datatype) === "table"
    );

    setSubGroupId(firstTableSg?._id ?? null);
  }, [isNotes, activeCat, notesData]);

  const showNotesDetails = (noteId) => {
    setSubMenuId(noteId);
  };

  const deletePopulateFS = async (subgroupID) => {
    try {
      const getParams = {
        category: activeCat,
        projectID: project_id,
        groupID: subMenuId,
        subgroupID: subgroupID,
      };

      await deletePopulateFSApiHandler(getParams);

      setUpdateTrigger((prev) => prev + 1);
      const delEl = document.getElementById(subgroupID);
      if (delEl) delEl.style.display = "none";
      return [];
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  // ── Re-sync notesData after a delete (updateTrigger bump) ─────────────────
  // Also reads from fsNotesResponseData, not rows
  useEffect(() => {
    if (!isNotes || activeCat === "checklist" || !subMenuId) {
      setNotesData(null);
      return;
    }
    const source = fsNotesResponseData ?? [];
    const selNote = source.find((item) => item._id === subMenuId) || null;
    setNotesData(selNote);
  }, [updateTrigger]);
  // ─────────────────────────────────────────────────────────────────────────

  const renderSubGroups = (notesData) => {
    const subGroups = notesData?.subgroup || notesData?.subGroup || [];

    const relatedTables = [];
    const normalTables = [];
    let relatedPartyTitle = "";

    subGroups.forEach((sg, idx) => {
      console.log("----- TABLE DEBUG -----");
      console.log("Category:", activeCat);
      console.log("Data Type:", sg?.dataType ?? sg?.datatype);
      console.log("Table Type:", sg?.tableType);
      console.log("Table Rows:", sg?.table?.length);
      console.log("Is Related Party:", sg?.tableType === "related party");
      console.log("-----------------------");

      const type = sg?.dataType ?? sg?.datatype ?? "";

      if (type === "table") {
        const formatIndianNumber = (val) => {
        if (val == null) return "";
        const num = Number(val);
        if (isNaN(num)) return val;

        if (num < 0) {
          return "(" + Math.abs(num).toLocaleString("en-IN") + ")";
        }
        return num.toLocaleString("en-IN");
      };

      const isNotesTable =
        activeCat === "BS notes" || activeCat === "PL notes";

      const tableItems = (Array.isArray(sg.table) ? sg.table : []).map((row) => {
        if (!isNotesTable) return row;

        const newRow = { ...row };

        Object.keys(newRow).forEach((key) => {
          if (
            key !== "_id" &&
            key !== "references" &&
            key !== "particular" &&
            key !== "lineType" &&
            newRow[key] !== null &&
            newRow[key] !== undefined &&
            !isNaN(Number(newRow[key]))
          ) {
            newRow[key] = formatIndianNumber(newRow[key]);
          }
        });

        return newRow;
      });

        if (!tableItems.length) return;

        const catRows = Object.keys(tableItems[0] || {});
        const filteredCols = catRows.filter((col) => col !== "_id");

        const orderedCols = isNotesTable
          ? filteredCols.filter((col) => col !== "references")  
          : [
              ...filteredCols.filter((col) => col !== "references"),
              ...(filteredCols.includes("references") ? ["references"] : []),
            ];

    const tableCols = orderedCols.map((col) => {
  if (col === "references") {
    return {
      label: col,
      field: col,
      align: "left",
      editable: false,
      type: "checklistref",
    };
  }

  const isNotesTable =
    activeCat === "BS notes" || activeCat === "PL notes";

  const lowerCol = col.toLowerCase();

  // ✅ Identify columns
  const isParticular = lowerCol === "particular";
  const isLineType =
    lowerCol === "linetype" || lowerCol === "line type";
if (isNotesTable) {
  return {
    label: col,
    field: col,

    align: isParticular || isLineType ? "left" : "right",

    editable: true,
    type: "text",


    width: isParticular
      ? 200
      : isLineType
      ? 200
      : 180,

    minWidth: isParticular
      ? 450
      : isLineType
      ? 200
      : 180,

    maxWidth: isParticular
      ? 450
      : isLineType
      ? 200
      : 180,

   
    headerStyle: {
      width: isParticular
        ? 450
        : isLineType
        ? 200
        : 180,
      minWidth: isParticular
        ? 450
        : isLineType
        ? 200
        : 180,
      maxWidth: isParticular
        ? 450
        : isLineType
        ? 200
        : 180,
      textAlign: isParticular || isLineType ? "left" : "right",
    },
  };
}
 
 
  const isPercentage =
    lowerCol.includes("percentage") || lowerCol.includes("%");

  const isTextDefault =
    lowerCol.includes("particular") ||
    lowerCol.includes("name") ||
    lowerCol.includes("description");

  const defaultAlign =
    isTextDefault && !isPercentage ? "left" : "right";

  return {
    label: col,
    field: col,
    align: defaultAlign,
    editable: true,
    type: isPercentage || defaultAlign === "right" ? "number" : "text",
    width: defaultAlign === "right" ? 200 : 300,
    minWidth: defaultAlign === "right" ? 200 : 300,
    maxWidth: defaultAlign === "right" ? 200 : 300,
  };
});
        const tableComponent = (
          <FSTable
            key={sg._id}
            columns={tableCols}
            rows={tableItems}
            onRowsChange={handleRowsChange}
            onCellChangeImmediate={handleTableEditDebounced}
            redirectTo={redirectTo}
            tableTitle={sg?.tableTitle}
            tableId={sg?._id}
            tableType={sg?.tableType}
            deletePopulateFS={deletePopulateFS}
          />
        );

        if (sg?.tableType === "related party") {
          const relatedTableComponent = (
            <div key={sg._id} className={bookclosurecss.relatedTableWrapper}>
              {activeCat === "disclosure" && (
                <div className={bookclosurecss.relatedTableTitle}>
                  {sg?.tableTitle || "Related party disclosures"}
                </div>
              )}
              <div
                className={bookclosurecss.relatedTableContent}
                style={{
                  borderRadius:
                    activeCat === "disclosure" ? "0px 0px 12px 12px" : "12px",
                }}
              >
                <FSTable
                  columns={tableCols}
                  rows={tableItems}
                  onRowsChange={handleRowsChange}
                  onCellChangeImmediate={handleTableEditDebounced}
                  redirectTo={redirectTo}
                  tableTitle={activeCat === "disclosure" ? null : sg?.tableTitle}
                  tableId={sg?._id}
                  tableType={sg?.tableType}
                  deletePopulateFS={deletePopulateFS}
                />
              </div>
            </div>
          );
          relatedTables.push(relatedTableComponent);
        } else {
          normalTables.push(tableComponent);
        }
      }

      if (type === "text") {
        normalTables.push(
          <div className="mt-5" key={`text-${idx}`}>
            <QuillReactEditor
              key={sg._id}   
              value={sg?.textRaw ?? ""}
              onChange={(content) => {
                handleTableEdit({
                  _id: sg?._id,
                  key: content.html,
                  value: content.text,
                  type: "text",
                });
              }}
            />
          </div>
        );
      }
    });

    return (
      <>
        {relatedTables.length > 0 && (
          <ViewRPTTransactions
            apiResponseData={fsNotesResponseData}
            getPopulateFSNotes={getPopulateFSNotes}
            notesData={notesData}
            tableTitle={relatedPartyTitle}
          >
            {relatedTables}
          </ViewRPTTransactions>
        )}
        {normalTables}
      </>
    );
  };

  const formatIndianNumber = (val) => {
  if (val == null) return "";
  const num = Number(val);
  if (isNaN(num)) return val;

  if (num < 0) {
    return "(" + Math.abs(num).toLocaleString("en-IN") + ")";
  }
  return num.toLocaleString("en-IN");
};

  const transformData = (data) => {
    const output = [];

    const walk = (node) => {
      if (node.level === 3) return;
      const type = node.level === 1 ? "header" : "row";

      output.push({
        _id: node._id,
        lineType: type,
        particular: node.name,
        notes: node.notes ?? null,
        currentYear: node.currentYear ?? null,
        previousYear: node.previousYear ?? null,
        references: node.references ?? [],
      });

      if (node.subgroup && node.subgroup.length) {
        node.subgroup.forEach(walk);
      }
    };

    data.forEach(walk);
    return output;
  };

  return (
    <>
      <div className={`${workSpaceCss.df}`}>
        <div className={`d-flex ${commoncss.pageContent}`}>
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 100, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          >
            <FSSideMenu
              title="Financial Statements"
              menuItems={menuItems}
              subMenu={subMenu}
              activeCat={activeCat}
              setActiveCat={setActiveCat}
              setSubMenuId={setSubMenuId}
              showNotesDetails={showNotesDetails}
              redirectNote={redirectNote}
              setRedirectNote={setRedirectNote}
              subMenuId={subMenuId}
            />
          </motion.div>
          <div
            className={bookclosurecss.bookClosureContent}
            style={{
              width:
                activeCat === "socie"
                  ? "83vw"
                  : activeCat === "checklist"
                  ? "83vw"
                  : "85vw",
            }}
           >
            <div className={`${bookclosurecss.bookClosureHeader} p-0`}>
              <FSTopBar history={history} activeCat={activeCat} />
            </div>

            <div
              style={{
                height: "88vh",
                overflowY: "auto",
                overflowX: "hidden",
                padding: "0px 20px",
                width: activeCat === "socie" ? "100%" : "auto",
              }}
            >
              {!isNotes && activeCat !== "checklist" && (
                <div className={activeCat === "socie" ? "socieScrollArea" : ""}>
                  <ShowDetails />
                </div>
              )}

              {isNotes && notesData && activeCat !== "checklist" && (
                <>{renderSubGroups(notesData)}</>
              )}

              {isNotes && subMenuId && activeCat === "checklist" && (
                <ShowChecklist subMenuId={subMenuId} />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StandaloneFinancialStatement;